import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  Typography,
  TableSortLabel,
  Button,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
  TextField,
  Divider,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import useDataTable from './hooks/useDataTable';
import DataTableFilters from './DataTableFilters';
import CopyButton from './columns/CopyButton';

const DataTable = forwardRef(
  (
    {
      columns = [],
      endpoint,
      initialSort,
      pageSize = 50,
      initialFilters = {},
      enableColumnPicker = true,
      enableSorting = true,
      enableFilters = false,
      enablePinning = false,
      renderToolbar,
      renderEmptyState,
      onRowClick,
      onRefresh,
      tableProps = {},
      testData = null,
      columnFilters = [],
      externalLoading = false,
      onFilter = null,
      externalFiltering = null,
      ...hookProps
    },
    ref,
  ) => {
    const {
      data,
      loading,
      error,
      pagination,
      sorting,
      filtering,
      hasMore,
      pinnedItems,
      handleSort,
      handleFilter,
      resetFilters,
      refresh,
      isColumnVisible,
      toggleColumn,
      setColumnVisibilityForAll,
      loadMore,
      togglePin,
      isPinned,
      unpinItem,
      clearAllPinned,
    } = useDataTable({
      endpoint,
      initialSort,
      pageSize,
      initialFilters,
      enablePinning,
      columnFilters,
      ...hookProps,
    });

    const tableContainerRef = useRef(null);

    useImperativeHandle(
      ref,
      () => ({
        refresh: refresh,
      }),
      [refresh],
    );

    const displayData = testData || data;
    const isStaticData = !!testData;
    const isLoading = loading || externalLoading;

    const currentFiltering =
      externalFiltering !== null ? externalFiltering : filtering;
    const currentHandleFilter = onFilter || handleFilter;

    const handleScroll = useCallback(() => {
      if (tableContainerRef.current) {
        const {
          scrollTop,
          scrollHeight,
          clientHeight,
        } = tableContainerRef.current;
        // INFO: consider margin of error, 10 px
        const sumPx = scrollTop + clientHeight;
        const margin = 20;
        if (sumPx > scrollHeight - margin && sumPx < scrollHeight + margin) {
          if (hasMore && !isLoading) {
            loadMore();
          }
        }
      }
    }, [hasMore, loadMore, isLoading]);

    useEffect(() => {
      const container = tableContainerRef.current;
      if (!container) return;

      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    useEffect(() => {
      if (columns.length > 0) {
        setColumnVisibilityForAll(columns);
      }
    }, [columns, setColumnVisibilityForAll]);

    const [columnPickerAnchor, setColumnPickerAnchor] = useState(null);
    const [columnSearch, setColumnSearch] = useState('');

    const visibleColumns = columns.filter((column) =>
      isColumnVisible(column.key),
    );

    const getSortDirection = (columnKey) => {
      if (sorting.key !== columnKey) return undefined;
      return sorting.order === 'asc' ? 'asc' : 'desc';
    };

    const filteredColumns = columns.filter((column) =>
      column.label.toLowerCase().includes(columnSearch.toLowerCase()),
    );

    const handleColumnPickerOpen = (event) => {
      setColumnPickerAnchor(event.currentTarget);
    };

    const handleColumnPickerClose = () => {
      setColumnPickerAnchor(null);
      setColumnSearch('');
    };

    const handleShowAll = () => {
      columns.forEach((column) => {
        if (!isColumnVisible(column.key)) {
          toggleColumn(column.key);
        }
      });
    };

    const handleHideAll = () => {
      columns.forEach((column) => {
        if (isColumnVisible(column.key)) {
          toggleColumn(column.key);
        }
      });
    };

    const renderCellContent = (column, row) => {
      const value = row[column.key];

      let content;

      if (column.render) {
        content = column.render(value, row, {
          togglePin,
          isPinned,
          unpinItem,
          clearAllPinned,
          pinnedItems,
          refresh,
        });
      } else {
        let formattedValue = value;

        if (column.format === 'currency') {
          formattedValue = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
          }).format(value || 0);
        }

        if (column.format === 'date') {
          formattedValue = value ? new Date(value).toLocaleDateString() : '–';
        }

        content = formattedValue;
      }

      if (
        column.copyable &&
        value &&
        value !== '-' &&
        value !== null &&
        value !== undefined
      ) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {content}
            <CopyButton text={value} />
          </Box>
        );
      }

      return content;
    };

    const renderEmptyStateDefault = () => (
      <TableRow>
        <TableCell
          colSpan={visibleColumns.length}
          align='center'
          sx={{ py: 8 }}>
          <Typography variant='body1' color='text.secondary'>
            No data available
          </Typography>
        </TableCell>
      </TableRow>
    );

    const renderLoadingStateDefault = () => (
      <TableRow>
        <TableCell
          colSpan={visibleColumns.length}
          align='center'
          sx={{ py: 8 }}>
          <CircularProgress />
        </TableCell>
      </TableRow>
    );

    const renderErrorState = () => (
      <TableRow>
        <TableCell
          colSpan={visibleColumns.length}
          align='center'
          sx={{ py: 8 }}>
          <Typography variant='body1' color='error'>
            {error}
          </Typography>
        </TableCell>
      </TableRow>
    );

    return (
      <Box sx={{ position: 'relative' }}>
        {renderToolbar && (
          <Box sx={{ mb: 2 }}>
            {renderToolbar({
              handleFilter: currentHandleFilter,
              filtering: currentFiltering,
              resetFilters,
            })}
          </Box>
        )}

        {enableFilters && (
          <>
            <DataTableFilters
              columnFilters={columnFilters}
              filtering={currentFiltering}
              onFilter={currentHandleFilter}
              onReset={resetFilters}
              onRefresh={onRefresh || refresh}
              enableColumnPicker={enableColumnPicker}
              onColumnPickerOpen={handleColumnPickerOpen}
              loading={isLoading}
            />

            {enableColumnPicker && (
              <Menu
                anchorEl={columnPickerAnchor}
                open={Boolean(columnPickerAnchor)}
                onClose={handleColumnPickerClose}
                PaperProps={{
                  sx: {
                    width: 280,
                    maxHeight: 400,
                  },
                }}>
                <Box sx={{ px: 1.5, py: 1, pb: 0.5 }}>
                  <TextField
                    placeholder='Find column'
                    value={columnSearch}
                    onChange={(e) => setColumnSearch(e.target.value)}
                    size='small'
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <Search
                          sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }}
                        />
                      ),
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                </Box>

                <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
                  {filteredColumns.map((column) => (
                    <MenuItem key={column.key} sx={{ py: 0.25, px: 1.5 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isColumnVisible(column.key)}
                            onChange={() => toggleColumn(column.key)}
                            size='small'
                          />
                        }
                        label={
                          <Typography
                            variant='body2'
                            sx={{ fontSize: '0.875rem' }}>
                            {column.label}
                          </Typography>
                        }
                        sx={{ width: '100%', margin: 0 }}
                      />
                    </MenuItem>
                  ))}
                </Box>

                <Divider />

                <Box
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}>
                  <Button
                    size='small'
                    onClick={handleHideAll}
                    sx={{
                      textTransform: 'uppercase',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                    }}>
                    Hide All
                  </Button>
                  <Button
                    size='small'
                    onClick={handleShowAll}
                    sx={{
                      textTransform: 'uppercase',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                    }}>
                    Show All
                  </Button>
                </Box>
              </Menu>
            )}
          </>
        )}

        <TableContainer
          component={Paper}
          ref={tableContainerRef}
          sx={{
            maxHeight: isStaticData ? 'none' : '60vh',
            overflow: 'auto',
          }}
          onScroll={isStaticData ? undefined : handleScroll}
          {...tableProps}>
          <Table stickyHeader={!isStaticData}>
            <TableHead>
              <TableRow>
                {visibleColumns.map((column) => (
                  <TableCell
                    key={column.key}
                    align={column.align || 'left'}
                    sortDirection={getSortDirection(column.key)}>
                    {enableSorting && column.sortable ? (
                      <TableSortLabel
                        active={sorting.key === column.key}
                        direction={getSortDirection(column.key)}
                        onClick={() => handleSort(column.key)}>
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading &&
                displayData.length === 0 &&
                pinnedItems.length === 0 &&
                renderLoadingStateDefault()}

              {error && !isLoading && renderErrorState()}

              {!isLoading &&
                !error &&
                displayData.length === 0 &&
                pinnedItems.length === 0 &&
                (renderEmptyState
                  ? renderEmptyState()
                  : renderEmptyStateDefault())}

              {!error &&
                enablePinning &&
                pinnedItems.length > 0 &&
                pinnedItems.map((row, index) => (
                  <TableRow
                    key={`pinned-${row.id || index}`}
                    hover={!!onRowClick}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    sx={{
                      cursor: onRowClick ? 'pointer' : 'default',
                      bgcolor: 'rgba(25, 118, 210, 0.08)',
                      borderLeft: '4px solid',
                      borderLeftColor: 'primary.main',
                      '&:hover': {
                        bgcolor: 'rgba(25, 118, 210, 0.12)',
                      },
                    }}>
                    {visibleColumns.map((column) => (
                      <TableCell
                        key={column.key}
                        align={column.align || 'left'}>
                        {renderCellContent(column, row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

              {!error &&
                displayData.length > 0 &&
                displayData
                  .filter((row) => !enablePinning || !isPinned(row.id))
                  .map((row, index) => (
                    <TableRow
                      key={row.id || index}
                      hover={!!onRowClick}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      sx={{
                        cursor: onRowClick ? 'pointer' : 'default',
                      }}>
                      {visibleColumns.map((column) => (
                        <TableCell
                          key={column.key}
                          align={column.align || 'left'}>
                          {renderCellContent(column, row)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}

              {loading && displayData.length > 0 && (
                <TableRow>
                  <TableCell
                    colSpan={visibleColumns.length}
                    align='center'
                    sx={{ py: 2 }}>
                    <CircularProgress size={24} />
                    <Typography variant='body2' sx={{ mt: 1 }}>
                      Loading more...
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!isStaticData && (
          <Box sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
            <Typography variant='body2' color='text.secondary'>
              {!hasMore && displayData.length > 0 && 'All records loaded'}
              {hasMore && displayData.length > 0 && 'Scroll down to load more'}
              {displayData.length > 0 &&
                ` • ${displayData.length} of ${pagination.total} records`}
            </Typography>
          </Box>
        )}
      </Box>
    );
  },
);

DataTable.displayName = 'DataTable';

export default DataTable;
